import React, { useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { Search, Send, Eye, Trophy, Target, BookOpen, CopyPlus } from "lucide-react";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { apiClient } from "../../apiClient";

const columnHelper = createColumnHelper();

const DSASheet = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentSheet = searchParams.get("name") || "striver";

  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");

  // Fetch the current sheet's problems
  const { data: problems = [], isLoading } = useQuery({
    queryKey: ["dsaSheet", currentSheet],
    queryFn: async () => {
      const res = await fetch(`https://orchestration.trackdsa.in/problems/${currentSheet}`);
      if (!res.ok) throw new Error("Failed to fetch sheet");
      const data = await res.json();
      return data.totalProblem || data.totalproblem; // Handle potential API capitalization variations
    },
  });

  // Bulk Add Mutation
  const { mutate: addProblems, isPending: isAdding } = useMutation({
    mutationFn: (problemsArray) => {
      // Clean up the data before sending (remove __v and _id to force new creation)
      const cleanProblems = problemsArray.map(({ __v, _id, ...rest }) => rest);
      
      return apiClient("/problems/addproblem", {
        method: "POST",
        body: JSON.stringify({ problems: cleanProblems }),
      });
    },
    onSuccess: () => {
      toast.success("Problems successfully added to your list!");
      setRowSelection({}); // Clear selection after success
    },
    onError: (err) => {
      toast.error(err.message || "Failed to add problems. Please login.");
    },
  });

  const handleBulkAdd = () => {
    if (!Cookies.get("accessToken")) {
      toast.error("Please login to save problems.");
      return;
    }
    
    const selectedRows = table.getSelectedRowModel().rows.map(r => r.original);
    
    if (selectedRows.length > 0) {
      addProblems(selectedRows);
    } else {
      // If nothing selected, add all filtered
      if (window.confirm(`Add all ${table.getPreFilteredRowModel().rows.length} problems to your list?`)) {
        addProblems(problems);
      }
    }
  };

  // Add this change to your existing handleSingleAdd
  const handleSingleAdd = useCallback((problem) => {
    if (!Cookies.get("accessToken")) {
      toast.error("Please login to save problems.");
      return;
    }
    addProblems([problem]);
  }, [addProblems]); // Only re-creates if addProblems changes

  const columns = useMemo(() => [
    columnHelper.accessor("title", {
      header: "Title",
      cell: (info) => <span className="font-semibold text-slate-800">{info.getValue()}</span>,
    }),
    columnHelper.accessor("difficulty", {
      header: "Difficulty",
      cell: (info) => {
        const val = info.getValue();
        const styles = {
          "1": "bg-emerald-100 text-emerald-800 border-emerald-200",
          "2": "bg-amber-100 text-amber-800 border-amber-200",
          "3": "bg-red-100 text-red-800 border-red-200"
        };
        const labels = { "1": "Easy", "2": "Medium", "3": "Hard" };
        return (
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${styles[val]}`}>
            {labels[val]}
          </span>
        );
      },
    }),
    columnHelper.accessor("topic", {
      header: "Topics",
      cell: (info) => (
        <div className="flex flex-wrap gap-1 max-w-[250px]">
          {info.getValue()?.map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-medium rounded-md uppercase tracking-wider">
              {tag}
            </span>
          ))}
        </div>
      ),
    }),
    columnHelper.display({
      id: "action",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center gap-4">
          <a href={row.original.url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-600 transition-colors" title="View Problem">
            <Eye className="w-4 h-4" />
          </a>
          <button 
            onClick={() => handleSingleAdd(row.original)}
            disabled={isAdding}
            className="text-slate-400 hover:text-emerald-600 transition-colors disabled:opacity-50"
            title="Move to My Problems"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      ),
    }),
    columnHelper.display({
      id: "action",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center gap-4">
          <a href={row.original.url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-600 transition-colors" title="View Problem">
            <Eye className="w-4 h-4" />
          </a>
          <button 
            onClick={() => handleSingleAdd(row.original)}
            disabled={isAdding}
            className="text-slate-400 hover:text-emerald-600 transition-colors disabled:opacity-50"
            title="Move to My Problems"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      ),
    }),
  ], [isAdding, handleSingleAdd]); // Added handleSingleAdd here!

  const table = useReactTable({
    data: problems,
    columns,
    state: { rowSelection, globalFilter },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 15 } },
  });

  const sheets = [
    { id: "striver", name: "Striver's SDE Sheet", icon: Trophy },
    { id: "lovebabbar", name: "Love Babbar 450", icon: Target },
    { id: "fraz", name: "Fraz's Sheet", icon: BookOpen },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      
      {/* Header & Tabs */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Curated DSA Sheets</h1>
          <p className="text-slate-500 mt-1">Select a popular curriculum and move problems to your personal tracker.</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2">
          {sheets.map((sheet) => {
            const isActive = currentSheet === sheet.id;
            const Icon = sheet.icon;
            return (
              <button
                key={sheet.id}
                onClick={() => {
                  setSearchParams({ name: sheet.id });
                  setRowSelection({});
                }}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all ${
                  isActive 
                    ? "bg-blue-600 text-white shadow-md" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? "text-blue-200" : "text-slate-400"}`} />
                {sheet.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
        
        {/* Controls Toolbar */}
        <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex flex-col sm:flex-row justify-between gap-4">
          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search in this sheet..."
              value={globalFilter ?? ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            />
          </div>

          <button
            onClick={handleBulkAdd}
            disabled={isAdding || isLoading}
            className="flex items-center justify-center gap-2 px-5 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm whitespace-nowrap"
          >
            <CopyPlus className="w-4 h-4" />
            {Object.keys(rowSelection).length > 0 
              ? `Add Selected (${Object.keys(rowSelection).length})` 
              : "Add All Displayed"}
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto min-h-[500px]">
          {isLoading ? (
            <div className="flex items-center justify-center h-64 text-slate-400 animate-pulse">
              Loading sheet data...
            </div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-600 font-medium border-b border-slate-200">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th 
                        key={header.id} 
                        onClick={header.column.getToggleSortingHandler()}
                        className="px-6 py-4 whitespace-nowrap cursor-pointer select-none hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{ asc: "↑", desc: "↓" }[header.column.getIsSorted()] ?? null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-slate-100">
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50 transition-colors group">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-6 py-4">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
                {table.getRowModel().rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                      No problems found matching your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div className="p-4 border-t border-slate-200 bg-slate-50/50 flex items-center justify-between text-sm text-slate-600">
          <div>
             Page <span className="font-medium text-slate-900">{table.getState().pagination.pageIndex + 1}</span> of{" "}
             <span className="font-medium text-slate-900">{table.getPageCount() || 1}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="px-3 py-1 rounded border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-50 transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="px-3 py-1 rounded border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DSASheet;